import { Flex, IconButton } from '@driftt/dds'
import { faGripDotsVertical } from '@driftt/dds.icons/regular/faGripDotsVertical'
import React, { useMemo } from 'react'
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd'

import { PersonaForm } from 'components/AIConfiguration/LeadQualification/PersonaCard/types'
import { PropertyDefinition } from 'types/contacts'

import AttributeRow from './AttributeRow'
import { TableHeader } from './AttributesDragDropTable/AttributesDragAndDropTable'

interface Props {
  fields: PersonaForm['attributes']
  contactAttributes: PropertyDefinition[]
  isDragAndDropEnable: boolean
  handleRemoveAttribute: (idx: number) => void
  handleClickRequiredAttribute: () => void
  handleDragAttribute: (result: DropResult) => void
}

const headerRow = [
  {
    label: null,
    value: 'drag n drop',
  },
  {
    label: 'Attribute',
    value: 'attribute',
  },
  {
    label: 'Condition',
    value: 'condition',
  },
  {
    label: 'Value',
    value: 'value',
  },
  {
    label: 'Required?',
    value: 'required',
  },
  {
    label: null,
    value: 'remove',
  },
]

const tableSizing = [
  '46px', // drag n drop
  '280px', // attribute
  '282px', // condition
  '277px', // value
  '75px', // required
  '12px', // remove
]

const Attributes = ({
  fields,
  contactAttributes,
  isDragAndDropEnable,
  handleRemoveAttribute,
  handleClickRequiredAttribute,
  handleDragAttribute,
}: Props) => {
  const attributeNameOptions = useMemo(() => {
    const formattedContactAttributes =
      contactAttributes.map(({ name, displayName, type }) => ({
        label: displayName,
        value: name,
        type,
        source: 'CONTACT',
      })) || []

    fields.forEach(({ name }) => {
      if (
        typeof name === 'string' &&
        !formattedContactAttributes.find(({ value }) => value === name)
      ) {
        formattedContactAttributes.push(name)
      }
    })
    return [
      {
        label: 'Contact Attributes',
        options: formattedContactAttributes,
      },
    ]
  }, [contactAttributes, fields])

  return (
    <>
      <Flex direction="column" fullWidth gap={0}>
        <TableHeader tableSizing={tableSizing} headerRow={headerRow} />
        <Flex fullWidth css={{ '> div': { width: '100%' } }}>
          <DragDropContext onDragEnd={handleDragAttribute}>
            <Droppable droppableId="test-items">
              {(provided, snapshot) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {fields.map((field, idx, arr) => (
                    <Draggable
                      key={`test[${idx}]`}
                      draggableId={`item-${idx}`}
                      index={idx}
                    >
                      {(provided, snapshot) => (
                        <Flex
                          gap={0}
                          fullWidth
                          ref={provided.innerRef}
                          key={`${field.name?.label}-${idx}`}
                          css={{
                            padding: '0 $1',
                            borderBottom: '1px solid $gray3',
                          }}
                          {...provided.draggableProps}
                        >
                          <Flex
                            gap={0}
                            css={{
                              width: '100%',
                              maxWidth: tableSizing[0],
                              marginTop: '$1',
                              marginRight: '$2',
                            }}
                          >
                            {isDragAndDropEnable ? (
                              <IconButton
                                title="change order"
                                icon={faGripDotsVertical}
                                css={{ cursor: 'grab' }}
                                {...provided.dragHandleProps}
                              />
                            ) : null}
                          </Flex>
                          <AttributeRow
                            idx={idx}
                            tableSizing={tableSizing}
                            isRemoveAvailable={arr.length > 1}
                            attributeNameOptions={attributeNameOptions}
                            handleRemoveAttribute={handleRemoveAttribute}
                            handleToggleRequiredAttribute={
                              handleClickRequiredAttribute
                            }
                          />
                        </Flex>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Flex>
      </Flex>
    </>
  )
}

export default Attributes
